import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, shareReplay, map, catchError, of, startWith, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';

interface GoogleMapsConfigResponse {
  googleMapsApiKey: string;
}

export interface FeatureFlags {
  showWineries: boolean;
  showWineryWines: boolean;
  showActivity: boolean;
  showWineTastings: boolean;
  showAppPromoOnMobile: boolean;
  showExperiences: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ApiConfigService {
  private googleMapsKey$?: Observable<string>;
  private featureFlags$?: Observable<FeatureFlags>;
  private readonly featureFlagsRefresh$ = new Subject<void>();

  constructor(private readonly http: HttpClient) {}

  getGoogleMapsApiKey(): Observable<string> {
    if (!this.googleMapsKey$) {
      this.googleMapsKey$ = this.http
        .get<GoogleMapsConfigResponse>(`${environment.apiBaseUrl}/config/google-maps-key`, {
          withCredentials: false
        })
        .pipe(
          map(response => response.googleMapsApiKey),
          shareReplay(1)
        );
    }
    return this.googleMapsKey$;
  }

  getFeatureFlags(): Observable<FeatureFlags> {
    if (!this.featureFlags$) {
      const baseFlags: FeatureFlags = {
        showWineries: false,
        showWineryWines: false,
        showActivity: false,
        showWineTastings: false,
        showAppPromoOnMobile: true,
        showExperiences: false
      };
      const environmentFlags = (environment.featureFlags ?? {}) as Partial<FeatureFlags>;
      const applyEnvironmentOverrides = (flags: FeatureFlags): FeatureFlags => ({
        ...flags,
        ...environmentFlags
      });
      const fallbackFlags = applyEnvironmentOverrides(baseFlags);

      this.featureFlags$ = this.featureFlagsRefresh$.pipe(
        startWith(undefined),
        switchMap(() =>
          this.http
            .get<FeatureFlags>(`${environment.apiBaseUrl}/config/feature-flags`, { withCredentials: false })
            .pipe(
              map(flags => applyEnvironmentOverrides({ ...baseFlags, ...flags })),
              catchError(() => of<FeatureFlags>(fallbackFlags))
            )
        ),
        shareReplay(1)
      );
    }
    return this.featureFlags$;
  }

  refreshFeatureFlags(): void {
    this.featureFlagsRefresh$.next();
  }
}
